import {
    collection,
    doc,
    getDocs,
    getFirestore,
    onSnapshot,
    serverTimestamp,
    writeBatch,
} from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { COLLECTIONS } from '@constants/firestorePaths';

function db() {
    return getFirestore(getFirebaseApp());
}

const BRANCHES_SUB = 'branches';

export function schoolTimeSummaryRef(tenantId) {
    if (!tenantId) throw new Error('Missing tenantId');
    return doc(db(), COLLECTIONS.SCHOOL_TIME, tenantId);
}

export function schoolTimeBranchesCollectionRef(tenantId) {
    if (!tenantId) throw new Error('Missing tenantId');
    return collection(db(), COLLECTIONS.SCHOOL_TIME, tenantId, BRANCHES_SUB);
}

/** Stable Firestore id for a new campus/branch document. */
export function createCampusBranchId(tenantId) {
    if (!tenantId) throw new Error('Missing tenantId');
    return doc(schoolTimeBranchesCollectionRef(tenantId)).id;
}

function mapBranchDoc(d) {
    const data = d.data() || {};
    return {
        id: d.id,
        branchName: data.branchName || '',
        address: data.address || '',
        effectiveFrom: data.effectiveFrom || null,
        schedule: data.schedule && typeof data.schedule === 'object' ? data.schedule : {},
    };
}

/**
 * Live campus/branch rows from `schoolTime/{tenantId}/branches/*` (canonical when present).
 */
export function subscribeSchoolTimeBranches({ tenantId, onNext, onError }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const ref = schoolTimeBranchesCollectionRef(tenantId);
    return onSnapshot(
        ref,
        (snap) => {
            const rows = snap.docs.map(mapBranchDoc);
            rows.sort((a, b) => (a.branchName || '').localeCompare(b.branchName || '', undefined, { sensitivity: 'base' }));
            onNext?.(rows);
        },
        (err) => onError?.(err)
    );
}

function countWorkingSlots(branches, dayKeys) {
    let n = 0;
    for (const b of branches || []) {
        if (!(b?.branchName || '').trim()) continue;
        for (const key of dayKeys) {
            if (b?.schedule?.[key]?.on) n += 1;
        }
    }
    return n;
}

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

/** True when every named campus has implement date + at least one working day + From/To for each on day. */
export function isAttendanceConfigComplete(cleanBranches) {
    const named = (cleanBranches || []).filter((b) => (b?.branchName || '').trim());
    if (!named.length) return false;
    for (const b of named) {
        if (!b.effectiveFrom) return false;
        const anyOn = DAY_KEYS.some((k) => b.schedule?.[k]?.on);
        if (!anyOn) return false;
        for (const k of DAY_KEYS) {
            const cur = b.schedule?.[k];
            if (!cur?.on) continue;
            if (!cur.from || !cur.to) return false;
        }
    }
    return true;
}

/**
 * Writes campus docs under `schoolTime/{tenantId}/branches`, summary analytics on
 * `schoolTime/{tenantId}`, and mirrors `attendanceConfig` on `tenants/{tenantId}` for attendance UI.
 *
 * @param {object} opts
 * @param {string} opts.tenantId
 * @param {object[]} opts.cleanBranches Normalized branch payloads (may be incomplete for draft saves).
 * @param {boolean} [opts.attendanceConfigured] When `true`, forces `attendanceConfig.configured` (use after full validation).
 *   When omitted, derives from {@link isAttendanceConfigComplete}.
 */
export async function persistSchoolTimeConfiguration({ tenantId, cleanBranches, attendanceConfigured }) {
    if (!tenantId) throw new Error('Missing tenantId');
    const branchesCol = schoolTimeBranchesCollectionRef(tenantId);
    const summaryRef = schoolTimeSummaryRef(tenantId);
    const tenantRef = doc(db(), COLLECTIONS.TENANTS, tenantId);

    const existingSnap = await getDocs(branchesCol);
    const nextIds = new Set((cleanBranches || []).map((b) => b.id).filter(Boolean));

    const batch = writeBatch(db());
    for (const d of existingSnap.docs) {
        if (!nextIds.has(d.id)) {
            batch.delete(doc(branchesCol, d.id));
        }
    }

    const named = (cleanBranches || []).filter((b) => (b?.branchName || '').trim());
    for (const b of cleanBranches || []) {
        const ref = doc(branchesCol, b.id);
        batch.set(
            ref,
            {
                tenantId,
                branchName: b.branchName || '',
                address: b.address || '',
                effectiveFrom: b.effectiveFrom || null,
                schedule: b.schedule || {},
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );
    }

    const configured =
        attendanceConfigured === true ? true : attendanceConfigured === false ? false : isAttendanceConfigComplete(cleanBranches);

    const attendanceConfig = {
        configured,
        branches: cleanBranches,
        updatedAt: new Date().toISOString(),
    };

    const workingSlots = countWorkingSlots(cleanBranches, DAY_KEYS);

    batch.set(
        summaryRef,
        {
            tenantId,
            configured,
            totalBranches: named.length,
            totalWorkingDaySlots: workingSlots,
            lastSavedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );

    batch.set(tenantRef, { attendanceConfig }, { merge: true });

    await batch.commit();
    return { attendanceConfig };
}
